import { Check, Store, ShoppingBasket, Coffee, Refrigerator, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const IMG_PANTRY =
  "https://images.unsplash.com/photo-1786398018373-d64916f263d6?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_OFFICE =
  "https://images.unsplash.com/photo-1759691555407-3c5834e6991e?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_COFFEE =
  "https://images.pexels.com/photos/7162356/pexels-photo-7162356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = {
  vending: [
    "Custom product mix — snacks, drinks, healthy lines",
    "Infrared anti-stuck guaranteed drop sensor",
    "Free installation with daily telemetry restocking",
  ],
  micro: [
    "Self-checkout kiosks, open 24/7",
    "Fresh salads, artisan wraps & gourmet meals",
    "Open-air grab-and-go convenience",
  ],
  coffee: [
    "Bean-to-cup espresso machines",
    "Subsidized or free employee pantry stock",
    "Local roast beans & oat milk replenishment",
  ],
  coolers: [
    "Tap your card or phone to unlock the door",
    "AI vision & weight-sensor item billing",
    "Sleek compact footprint for modern offices",
  ],
};

const Feat = ({ items }) => (
  <ul className="space-y-3">
    {items.map((f) => (
      <li key={f} className="flex items-start gap-3 text-sm leading-relaxed text-stone-600">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#A8861D]/12">
          <Check className="h-3 w-3 text-[#A8861D]" />
        </span>
        {f}
      </li>
    ))}
  </ul>
);

const Card = ({ id, icon: Icon, title, children, className = "" }) => (
  <div
    data-testid={`service-card-${id}`}
    className={`group relative flex flex-col overflow-hidden rounded-3xl border border-black/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#1B365D]/40 hover:shadow-[0_24px_70px_rgba(27,54,93,0.12)] ${className}`}
  >
    {children}
    <div className="relative z-10 flex flex-1 flex-col p-7">
      <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1B365D]/10 text-[#1B365D]">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="font-display text-xl font-bold tracking-tight text-[#16120E] sm:text-2xl">
        {title}
      </h3>
    </div>
  </div>
);

export default function Services() {
  return (
    <section id="services" data-testid="services-bento-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <Eyebrow>What we do</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-[#16120E] sm:text-4xl lg:text-5xl">
              One partner for every craving.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-sm leading-relaxed text-stone-600 sm:text-base">
              From a single snack machine to a full micro market, we design, install
              and manage the whole thing — at no cost to your business.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-8 lg:grid-cols-12">
          <Reveal className="md:col-span-8 lg:col-span-7">
            <div
              data-testid="service-card-full-vending"
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-black/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#1B365D]/40 hover:shadow-[0_24px_70px_rgba(27,54,93,0.12)]"
            >
              <div className="relative h-52 overflow-hidden sm:h-60">
                <img
                  src={IMG_OFFICE}
                  alt="Modern office lounge where SnackSavvy vending operates"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, #FFFFFF 4%, rgba(255,255,255,0.3) 55%, transparent)",
                  }}
                />
              </div>
              <div className="relative z-10 flex flex-1 flex-col p-7 pt-2">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1B365D]/10 text-[#1B365D]">
                  <Store className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#16120E] sm:text-2xl">
                  Full-Service Vending
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-500">
                  Our flagship SnackPod™ machines — installed free, stocked on
                  telemetry, managed end-to-end.
                </p>
                <div className="mt-5">
                  <Feat items={FEATURES.vending} />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08} className="md:col-span-8 lg:col-span-5">
            <div
              data-testid="service-card-micro-market"
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-black/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#1B365D]/40 hover:shadow-[0_24px_70px_rgba(27,54,93,0.12)]"
            >
              <div className="relative flex flex-1 flex-col p-7">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C9A227]/15 text-[#1B365D]">
                  <ShoppingBasket className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#16120E] sm:text-2xl">
                  Micro Markets
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-500">
                  A self-serve corner store inside your office — no staff required.
                </p>
                <p className="mt-3 rounded-xl border border-[#C9A227]/30 bg-[#C9A227]/8 px-3.5 py-2.5 text-xs leading-relaxed text-[#7A6215]">
                  Needs major electrical or construction work? We flag it in the free
                  survey — those contractor costs are quoted separately, not on us.
                </p>
                <div className="mt-5">
                  <Feat items={FEATURES.micro} />
                </div>
              </div>
              <div className="relative h-44 overflow-hidden">
                <img
                  src={IMG_PANTRY}
                  alt="Micro market shelves stocked with fresh food and snacks"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to bottom, #FFFFFF 0%, rgba(255,255,255,0.15) 45%, transparent)",
                  }}
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.05} className="md:col-span-4 lg:col-span-6">
            <div
              data-testid="service-card-coffee-pantry"
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-black/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#1B365D]/40 hover:shadow-[0_24px_70px_rgba(27,54,93,0.12)]"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={IMG_COFFEE}
                  alt="Barista-grade coffee station installed by SnackSavvy"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, #FFFFFF 2%, rgba(255,255,255,0.25) 60%, transparent)",
                  }}
                />
              </div>
              <div className="relative z-10 flex flex-1 flex-col p-7 pt-3">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1B365D]/10 text-[#1B365D]">
                  <Coffee className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#16120E] sm:text-2xl">
                  Office Coffee & Pantry
                </h3>
                <div className="mt-4">
                  <Feat items={FEATURES.coffee} />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="md:col-span-4 lg:col-span-6">
            <div
              data-testid="service-card-smart-coolers"
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white to-[#F3E7D8] transition-all duration-300 hover:-translate-y-1 hover:border-[#1B365D]/40 hover:shadow-[0_24px_70px_rgba(27,54,93,0.12)]"
            >
              <div className="relative z-10 flex flex-1 flex-col p-7">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C9A227]/15 text-[#1B365D]">
                  <Refrigerator className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#16120E] sm:text-2xl">
                  Smart Coolers
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-500">
                  The next generation of grab-and-go — no checkout, no queues.
                </p>
                <div className="mt-5">
                  <Feat items={FEATURES.coolers} />
                </div>
              </div>
              <button
                onClick={() => scrollToSection("quote")}
                className="relative z-10 mx-7 mb-7 mt-auto inline-flex w-fit items-center gap-2 rounded-full border border-[#1B365D]/30 px-5 py-2.5 text-sm font-semibold text-[#1B365D] transition-all hover:-translate-y-0.5 hover:bg-[#1B365D]/10"
              >
                Ask about smart coolers <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
